import { useCallback, useEffect, useState } from 'react';

import { visitIQAiAutoMode } from '~/constants/localStorage';

const autoModeKey = (userId: string) => `${visitIQAiAutoMode}-${userId}`;

export const readStoredAutoMode = (userId: string) => {
  try {
    return localStorage.getItem(autoModeKey(userId)) === 'true';
  } catch {
    return false;
  }
};

export const writeStoredAutoMode = (userId: string, isAutoMode: boolean) => {
  try {
    localStorage.setItem(autoModeKey(userId), isAutoMode ? 'true' : 'false');
  } catch {
    // Storage unavailable (e.g. blocked site data): the toggle still works for this page load.
  }
};

interface UserAutoMode {
  userId?: string;
  isAutoMode: boolean;
}

const loadUserAutoMode = (userId?: string): UserAutoMode => ({
  userId,
  isAutoMode: userId ? readStoredAutoMode(userId) : false,
});

/**
 * The AI Insights auto mode toggle: a per-user preference kept in browser storage, so it stays on
 * across conversations and reloads. The state is tied to the user it was read for, so a user switch
 * never shows the previous user's value, and it stays off (not toggleable) until the user is known.
 */
export const useAutoMode = (userId?: string) => {
  const [userAutoMode, setUserAutoMode] = useState(() => loadUserAutoMode(userId));

  // Re-read during render when the user changes (React's "adjust state on prop change" pattern), so no
  // render is committed with the previous user's value.
  let current = userAutoMode;
  if (current.userId !== userId) {
    current = loadUserAutoMode(userId);
    setUserAutoMode(current);
  }

  useEffect(() => {
    if (userAutoMode.userId) writeStoredAutoMode(userAutoMode.userId, userAutoMode.isAutoMode);
  }, [userAutoMode]);

  // Stable across toggles so the memoized input doesn't re-render; persisting happens in the effect above.
  const toggleAutoMode = useCallback(() => {
    if (!userId) return;
    setUserAutoMode((previous) => ({ ...previous, isAutoMode: !previous.isAutoMode }));
  }, [userId]);

  return { isAutoMode: current.isAutoMode, isAutoModeReady: Boolean(userId), toggleAutoMode };
};
